import React, { useContext, useEffect, useState } from 'react'
import {
  Container,
  ElementImage,
  ElementName,
  Section,
  SectionElement,
  SectionElements,
  SectionTitle,
  StyledLink,
} from './style'
import arrow from '../../../assets/home/arrow.svg'
import { TypeMainOutlet } from '..'
import { useOutletContext } from 'react-router-dom'
import UserContext from '../../../contexts/UserContext'
import { useTenant } from '../../../contexts/TenantContext'
import {
  getModulesBySectionWithPermissions,
  getModulesForTenant,
  hasModulePermission,
  ModuleConfig,
} from '../../../config/modulesByTenant'
import SectionContext from '../../../contexts/SectionContext'
import { ListFilter, PlusCircle, SquarePen, Trash2 } from 'lucide-react'
import CreatePipelineModal from '../../../components/Pipelines/Modals/CreatePipelineModal'
import axios from '../../../config/axios'

const HomeScreen: React.FC = () => {
  // Capturando search e setSearch do OutletContext
  const { search, setSearch } = useOutletContext<TypeMainOutlet>()
  
  const { user } = useContext(UserContext)
  const { tenantId } = useTenant()
  const { permissions } = useContext(SectionContext)
  const [createPipelineModal, setCreatePipelineModal] = useState(false)
  const [pipelines, setPipelines] = useState<{ id: number, name: string }[]>([])

  const handleRenamePipeline = async (pipelineId: number, currentName: string) => {
    const newName = window.prompt('Digite o novo nome do funil', currentName)
    const trimmed = newName?.trim()
    if (!trimmed || trimmed === currentName) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}`, { name: trimmed })
      const updatedName = response.data?.data?.name || trimmed
      setPipelines(prev => prev.map(p => p.id === pipelineId ? { ...p, name: updatedName } : p))
    } catch (error) {
      console.error('Erro ao renomear funil:', error)
    }
  }

  const handleDeletePipeline = async (pipelineId: number, pipelineName: string) => {
    const confirmed = window.confirm(`Deseja excluir o funil "${pipelineName}"?`)
    if (!confirmed) return
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}`)
      setPipelines(prev => prev.filter(p => p.id !== pipelineId))
    } catch (error) {
      console.error('Erro ao excluir funil:', error)
    }
  }

  useEffect(() => {
    setSearch('')
    const fetchPipelines = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`)
        if (response.data) {
          setPipelines(response.data.data)
        }
      } catch (error) {
        console.warn('⚠️ Serviço de pipelines indisponível:', error)
        setPipelines([])
      }
    }
    fetchPipelines()
  }, [])

  console.log('HomeScreen Debug:', {
    hasUser: !!user,
    userId: user?.id,
    userSub: user?.sub,
    userRole: user?.role,
    userPermissions: user?.permissions,
    sectionPermissions: permissions,
    tenantId,
    hasUserPermissions:
      Array.isArray(user?.permissions) && user.permissions.length > 0,
    hasSectionPermissions: Array.isArray(permissions) && permissions.length > 0,
    finalPermissionsWillBe:
      user?.permissions &&
      Array.isArray(user.permissions) &&
      user.permissions.length > 0
        ? 'token permissions'
        : permissions && Array.isArray(permissions) && permissions.length > 0
        ? 'section permissions'
        : tenantId === 2
        ? 'fashion fallback'
        : 'industrial fallback',
  })

  // Se tenantId for explicitamente null, aguardamos
  if (tenantId === null) {
    return <Container>Carregando tenant...</Container>
  }

  // Determinar permissões finais (Lógica Original Mantida)
  let finalPermissions: string[]

  if (
    user?.permissions &&
    Array.isArray(user.permissions) &&
    user.permissions.length > 0
  ) {
    finalPermissions = user.permissions
    console.log('Usando permissões do token:', finalPermissions)
  } else if (
    permissions &&
    Array.isArray(permissions) &&
    permissions.length > 0
  ) {
    finalPermissions = permissions
  } else {
    console.log('Usuário sem permissões definidas')

    // Retornar tela de sem permissões
    return (
      <Container>
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '16px',
          }}
        >
          <h3 style={{ color: '#d32f2f', marginBottom: '16px' }}>
            Acesso Negado
          </h3>
          <p style={{ marginBottom: '8px' }}>
            Usuário sem permissões definidas.
          </p>
          <p style={{ marginBottom: '16px' }}>
            Entre em contato com o administrador do sistema.
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            Usuário: {user?.name || user?.email || 'N/A'} (ID:{' '}
            {user?.sub || 'N/A'})
          </p>
        </div>
      </Container>
    )
  }

  const modulesBySection = getModulesBySectionWithPermissions(tenantId, finalPermissions)

  // --- NOVA LÓGICA DE FILTRO ---
  
  // 1. Filtra os módulos (Roteirização, Estoque, etc)
  const filteredModulesBySection = Object.entries(modulesBySection).reduce((acc, [sectionName, modules]) => {
    const filtered = modules.filter(module =>
      module.name.toLowerCase().includes(search.toLowerCase())
    )
    if (filtered.length > 0) {
      acc[sectionName] = filtered
    }
    return acc
  }, {} as typeof modulesBySection)

  // 2. Filtra os Pipelines (Funis)
  const filteredPipelines = pipelines.filter(pipeline =>
    pipeline.name.toLowerCase().includes(search.toLowerCase())
  )

  // Verifica se a busca resultou em algo
  const hasResults = Object.keys(filteredModulesBySection).length > 0 || filteredPipelines.length > 0

  return (
    <Container>
      {/* Renderiza seções de módulos filtrados */}
      {Object.entries(filteredModulesBySection).map(([sectionName, modules]) => (
        <Section key={sectionName}>
          <SectionTitle>{sectionName}</SectionTitle>
          <SectionElements>
            {modules.map((module, index) => (
              <StyledLink key={index} to={`/panel/${module.path}`}>
                <SectionElement>
                  <ElementImage src={module.image} alt='icon' />
                  <ElementName>{module.name}</ElementName>
                  <ElementImage
                    className='arrow'
                    src={arrow}
                    alt='arrow-icon'
                  />
                </SectionElement>
              </StyledLink>
            ))}
          </SectionElements>
        </Section>
      ))}

      {/* Renderiza seção de Funis se houver resultados ou se a busca estiver vazia */}
      {(filteredPipelines.length > 0 || (search === '' && pipelines.length >= 0)) && (
        <Section>
          <SectionTitle>Funis</SectionTitle>
          <SectionElements>
            {/* O botão de criar só aparece quando não há busca ativa para não poluir */}
            {search === '' && (
              <SectionElement onClick={() => setCreatePipelineModal(true)} style={{ backgroundColor: '#E6F4FB', border: '1px solid #0090D3' }}>
                <PlusCircle size={30} color='#0090D3' />
                <ElementName>Criar Funil</ElementName>
                <ElementImage className='arrow' src={arrow} alt='arrow-icon' />
              </SectionElement>
            )}

            {filteredPipelines.map((pipeline) => (
              <StyledLink key={pipeline.id} to={`/panel/pipeline?pipelineId=${pipeline.id}`}>
                <SectionElement>
                  <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRenamePipeline(pipeline.id, pipeline.name) }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                      title='Renomear funil'
                    >
                      <SquarePen size={18} color='#0090D3' />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeletePipeline(pipeline.id, pipeline.name) }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                      title='Excluir funil'
                    >
                      <Trash2 size={18} color='#dc3545' />
                    </button>
                  </div>
                  <ListFilter size={30} color='#0090D3' />
                  <ElementName>{pipeline.name}</ElementName>
                  <ElementImage className='arrow' src={arrow} alt='arrow-icon' />
                </SectionElement>
              </StyledLink>
            ))}
          </SectionElements>
        </Section>
      )}

      {/* Feedback caso a busca não encontre nada */}
      {!hasResults && search !== '' && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
          {`Nenhuma aplicação ou funil encontrado para "${search}"`}
        </div>
      )}

      {createPipelineModal && <CreatePipelineModal closeModal={() => setCreatePipelineModal(false)} newPipeline={setPipelines} />}
    </Container>
  )
}

export default HomeScreen
